/**
 * Server-authored purchase quote for a membership/pass (D12).
 *
 * The subscription twin of `useBookingQuote`. One hook still serves both the
 * authenticated buyer (Review step) and the guest (FinishAccount step), but it
 * now picks the ROUTE by `asMember`: a signed-in caller gets
 * `/subscriptions/quote/me`, which evaluates a promo code against their
 * identity, and everyone else gets the tenant-only preview.
 *
 * That is the whole difference. Same body, same response type, same rendering —
 * only whether a code APPLIES can change, which is what lets a member find out
 * their per-customer cap is spent while they are still choosing instead of
 * after entering a card.
 *
 * `asMember` is part of the query key; see `keys.ts` for why that reverses an
 * earlier rule.
 *
 * @module subscription/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { subscriptionService } from '../api/subscriptionService';
import type { SubscriptionQuoteType } from '../types';

export interface UseSubscriptionQuoteOptions {
  clubId: number | null | undefined;
  subscriptionType: SubscriptionQuoteType | null | undefined;
  subscriptionId: number | null | undefined;
  /**
   * Captured promo code (7a) — optional, never gates the query. Part of the
   * query KEY: the discounted and list-price quotes are different server
   * answers, and the auto-drop flow relies on the key change to requote.
   */
  promoCode?: string | null;
  /**
   * How many passes (gifting). Part of the KEY for the same reason the code is:
   * two quantities are two server answers. Sent — and keyed — only above 1, so a
   * single-pass checkout keeps the exact key and request it has always had.
   */
  quantity?: number;
  /**
   * Quote as the signed-in buyer. Defaults to false so every existing caller
   * keeps the guest behaviour it has today; the funnel passes `isAuthenticated`.
   * Part of the KEY — a member answer and a guest answer are different server
   * answers once a code is capped per customer.
   */
  asMember?: boolean;
  enabled?: boolean;
}

export function useSubscriptionQuote(options: UseSubscriptionQuoteOptions) {
  const { clubId, subscriptionType, subscriptionId, promoCode, asMember = false, enabled = true } = options;
  const quantity = options.quantity !== undefined && options.quantity > 1 ? options.quantity : undefined;

  const hasRequiredParams = !!clubId && !!subscriptionType && !!subscriptionId;

  return useQuery({
    queryKey: hasRequiredParams
      ? queryKeys.subscriptions.quote(clubId, {
          subscriptionType,
          subscriptionId,
          ...(promoCode && { promoCode }),
          ...(quantity && { quantity }),
          // Keyed only when true, so a guest keeps the exact key it has always
          // had and nothing about the anonymous funnel moves.
          ...(asMember && { asMember: true }),
        })
      : queryKeys.disabled.subscriptionQuote(),
    queryFn: async () => {
      if (!clubId || !subscriptionType || !subscriptionId) {
        throw new Error('Required parameters missing');
      }

      const result = await subscriptionService.quoteSubscriptionPurchase(
        {
          clubId,
          subscriptionType,
          subscriptionId,
          ...(promoCode && { promoCode }),
          ...(quantity && { quantity }),
        },
        { asMember },
      );

      // A `success: false` here is a transport or contract failure, not a refusal —
      // the server answers 200 with `isValid: false` when the plan cannot be bought.
      // Throwing puts it on React Query's error path, where the caller blocks submit.
      // (Documented deviation from the internalapi-envelope hook rule, matching
      // useBookingQuote: nothing business-level is flattened by this throw.)
      if (!result.success) {
        throw new Error(result.message || 'Purchase quote failed');
      }

      return result.payload;
    },
    enabled: enabled && hasRequiredParams,
    ...cachePresets.frequent,
  });
}
